import { GripVerticalIcon, TrashIcon } from "lucide-react";
import { Button } from "@/modern/components/ui/button";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { SortableItemHandle } from "@/modern/components/ui/sortable";
import { getViewStateIconByRecord, showConfirmationDialog } from "@/other/utils/utils";
import { MessageDialog } from "@bryntum/schedulerpro";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { nonSupportedViewsForViewState } from "@/other/constants/OtherKeys";

interface ViewOrganizerRowProps {
  view: any;
  project: any;
  user: any;
  editorHandler: any;
  viewManager: any;
  canEdit: boolean;
  onDelete: (view: any) => void;
  onToggleVisibility: (view: any) => void;
}

export const ViewOrganizerRow = ({
  view,
  project,
  user,
  editorHandler,
  viewManager,
  canEdit,
  onDelete,
  onToggleVisibility
}: ViewOrganizerRowProps) => {
  const displayIconClass = getViewStateIconByRecord(view, project);

  const handleDoubleClick = () => {
    if (nonSupportedViewsForViewState.includes(view.viewType)) {
      MessageDialog.alert({
        title: "Unsupported View Type",
        message: "Editing is not available for this view type."
      });
      return;
    }

    const activeView = viewManager.renderState.selectedView;
    const isActiveView = view.id === activeView.id;

    editorHandler.openEditor(
      EDITOR_KEYS.viewInspectorEditor,
      null,
      { viewRecord: isActiveView ? activeView : view, canUseRecordViewState: !isActiveView },
      { by: "recordType", useExisting: true }
    );
  };

  const handleDelete = () => {
    if (view.isDefaultView) {
      MessageDialog.alert({
        title: "View Delete Not Allowed",
        message: "Swivu views cannot be deleted."
      });
      return;
    }

    if (view.isSharedView && (!user.can(GRANTS.grant_one) && view.creatorUserId !== user.id)) {
      MessageDialog.alert({
        title: "View Share Not Allowed",
        message: `Shared View created by ${view.creatorUserName}. Only creator of this shared view or a board admin can delete this view.`
      });
      return;
    }

    showConfirmationDialog({
      title: "Delete View",
      message: `Are you sure, You want to delete a view called "${view.name}"?`,
      confirmBtnText: "Delete",
      cancelBtnText: "Cancel",
      isCloseByHeader: false
    }).then((isConfirmed) => {
      if (isConfirmed) {
        onDelete(view);
      }
    });
  };

  const getCreatorAvatar = () => {
    if (view.isDefaultView) {
      return (
        <div className="w-8 h-8 flex items-center justify-center">
          <img
            src="/assets/Swivu-logo-grey.png"
            alt="Swivu"
            className="w-6 h-6 object-contain"
          />
        </div>
      );
    }

    const creatorBoardUser = project.boardUsersStore.find(
      (boardUser: any) => boardUser.userId === view.creatorUserId
    );

    if (creatorBoardUser?.profileImageURL) {
      return (
        <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex-shrink-0">
          <img
            src={creatorBoardUser.profileImageURL}
            alt={creatorBoardUser.name}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    if (creatorBoardUser?.name) {
      const initials = creatorBoardUser.name
        .split(" ")
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

      return (
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
          {initials}
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex items-center gap-3 p-3 group bg-card rounded-lg border border-border/40 hover:border-border transition-colors">
      {/* Drag Handle */}
      <SortableItemHandle value={view.id} draggable={canEdit}>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
          disabled={!canEdit}
        >
          <GripVerticalIcon className="size-4 text-muted-foreground" />
        </Button>
      </SortableItemHandle>

      {/* Icon */}
      <div
        className="w-12 flex items-center justify-center"
        style={{ color: view.color || "inherit" }}
      >
        <i className={`${displayIconClass} text-lg`} />
      </div>

      {/* Name */}
      <div
        className="flex-1 min-w-0 cursor-pointer"
        onDoubleClick={handleDoubleClick}
      >
        <div className="text-sm font-medium truncate hover:text-primary transition-colors">
          {view.name}
        </div>
      </div>

      {/* Visibility Checkbox */}
      <div className="flex items-center gap-2">
        <Checkbox
          checked={view.isVisible}
          onCheckedChange={() => onToggleVisibility(view)}
          disabled={!canEdit}
        />
      </div>

      {/* Creator Avatar */}
      <div className="flex items-center justify-center w-12">
        {getCreatorAvatar()}
      </div>

      {/* Delete Button */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={handleDelete}
        disabled={!canEdit}
      >
        <TrashIcon className="size-4 text-muted-foreground hover:text-destructive" />
      </Button>
    </div>
  );
};
